import { getErrorCode } from '@/utils/audience-builder'

/**
 * Audiences that already exist in the customer's ad account, chosen for the ad sets of a funnel stage. A chosen
 * audience is kept in `salesFunnelSetup.accountAudiences`, by wizard id, so the later steps can show it like any
 * other audience; its wizard id goes to `audienceSelections` of each stage it is chosen for. At launch the row
 * carries the stage's catalogue audience id and the Meta id of the audience.
 */

export const ACCOUNT_AUDIENCE_ID_PREFIX = 'acct-'
export const ACCOUNT_AUDIENCE_NAME_LIMIT = 60
export const ACCOUNT_AUDIENCE_SEARCH_MIN_LENGTH = 2
/** Shown for an audience of an existing ad set whose name is not known. */
export const ACCOUNT_AUDIENCE_FALLBACK_NAME = 'Audience from your ad account'

export const ACCOUNT_AUDIENCE_TEXT = {
    button: 'Audiences from your ad account',
    title: 'Audiences from your ad account',
    description:
        'Choose audiences that already exist in your Meta ad account. Only the people in the chosen audience see the ad set. Meta checks each audience again when the campaign is published.',
    searchLabel: 'Search audiences',
    searchPlaceholder: 'Search by name',
    filterLabel: 'Audience type',
    allTypes: 'All types',
    fetch: 'Fetch audiences from my ad account',
    fetching: 'Fetching audiences',
    loading: 'Loading your audiences',
    empty: 'No audiences found in your ad account yet. Fetch them again once you have created some in Meta.',
    noMatch: 'No audiences match your search.',
    upToDate: 'Your audiences are already up to date.',
    incomplete: 'Only the first audiences of your ad account are listed.',
    selected: 'Selected',
    usable: 'Ready',
    populating: 'Filling up',
    notUsable: 'Not available',
    done: 'Done',
    sizeUnknown: 'size not reported',
}

/** Why an audience cannot be chosen, by the reason code of the server. */
export const ACCOUNT_AUDIENCE_REASON_TEXT = {
    TOO_SMALL: 'Too small for Meta to show ads to yet',
    FLAGGED: 'Flagged by Meta, so it cannot be used for ads',
    FAILED: 'Meta could not build this audience',
    UNUSABLE: 'Meta does not allow this audience to be used for ads',
    STATUS_UNAVAILABLE: 'Meta did not report the status of this audience. Fetch your audiences again',
    MISSING: 'No longer in your ad account',
}

const UNKNOWN_REASON_TEXT = 'Not available for targeting'

/** A note for an audience that can be chosen, by the note code of the server. */
export const ACCOUNT_AUDIENCE_NOTE_TEXT = {
    POPULATING: 'Still filling, can be used',
    LONG_UNUSED: 'Meta reports this audience has not been used for a long time. You can still choose it.',
    EXPIRING: 'Meta reports this audience is about to expire. You can still choose it.',
}

/** What each status badge means, by the reason or note code of the server; `READY` is an audience without a note. */
export const ACCOUNT_AUDIENCE_STATUS_HINT = {
    READY: 'Meta reports this audience as ready to show ads to.',
    POPULATING:
        'Meta is still finding the people who fit this audience. You can use it now, and its size grows as it fills.',
    LONG_UNUSED:
        'Meta marks this audience as out of date because it has not been used in an ad for a while. It can still be used.',
    EXPIRING:
        'This audience has not been used in an active ad set for a long time. Meta may remove it if it stays unused.',
    TOO_SMALL: 'This audience is too small for Meta to show ads to yet.',
    FLAGGED: 'Meta flagged this audience for policy reasons, so it cannot be used for ads.',
    FAILED: 'Meta could not build this lookalike audience. Create it again in Meta Ads Manager.',
    UNUSABLE: 'Meta reports this audience as not usable for ads.',
    STATUS_UNAVAILABLE: 'Meta did not report the status of this audience. Fetch your audiences again to check it.',
    MISSING: 'This audience is no longer in your ad account.',
}

export const ACCOUNT_AUDIENCE_SUBTYPE = {
    website: 'Website visitors',
    engagement: 'Page and Instagram engagement',
    customerList: 'Customer list',
    lookalike: 'Lookalike',
    other: 'Other',
}

const SUBTYPE_GROUPS = {
    WEBSITE: 'website',
    ENGAGEMENT: 'engagement',
    IG_BUSINESS: 'engagement',
    VIDEO: 'engagement',
    CUSTOM: 'customerList',
    LOOKALIKE: 'lookalike',
}

/** The type group of a Meta subtype: website, engagement, customerList, lookalike or other. */
export const accountAudienceSubtypeGroup = (subtype) => SUBTYPE_GROUPS[String(subtype || '').toUpperCase()] || 'other'

export const accountAudienceSubtypeLabel = (subtype) => ACCOUNT_AUDIENCE_SUBTYPE[accountAudienceSubtypeGroup(subtype)]

export const accountAudienceReasonText = (reason) => ACCOUNT_AUDIENCE_REASON_TEXT[reason] || UNKNOWN_REASON_TEXT

export const accountAudienceNoteText = (note) => ACCOUNT_AUDIENCE_NOTE_TEXT[note] || null

/** The badge text of an audience: Ready, Filling up or Not available. */
export const accountAudienceStatusLabel = (audience) => {
    if (!audience?.usable) return ACCOUNT_AUDIENCE_TEXT.notUsable
    return audience.note === 'POPULATING' ? ACCOUNT_AUDIENCE_TEXT.populating : ACCOUNT_AUDIENCE_TEXT.usable
}

/** The short explanation of an audience's status badge. */
export const accountAudienceStatusHint = (audience) => {
    if (!audience?.usable) return ACCOUNT_AUDIENCE_STATUS_HINT[audience?.reason] || UNKNOWN_REASON_TEXT
    return ACCOUNT_AUDIENCE_STATUS_HINT[audience.note] || ACCOUNT_AUDIENCE_STATUS_HINT.READY
}

/** "last 365 days" from the retention window, or null when none is set. */
export const accountAudienceRetentionText = (days) => {
    const count = Number(days)
    if (days === null || days === undefined || !Number.isFinite(count) || count <= 0) return null
    return count === 1 ? 'last 1 day' : `last ${Math.round(count).toLocaleString('en-US')} days`
}

/** "about 1,200 people" from the lower bound of the size, or a plain note when Meta did not report one. */
export const accountAudienceSizeText = (lower) =>
    typeof lower === 'number' && lower >= 0
        ? `about ${Math.round(lower).toLocaleString('en-US')} people`
        : ACCOUNT_AUDIENCE_TEXT.sizeUnknown

/** Audiences whose name contains the search text; a text shorter than two characters finds everything. */
export const filterAccountAudiences = (audiences, { search = '', subtype = '' } = {}) => {
    const term = String(search || '')
        .trim()
        .toLowerCase()
    return (audiences || []).filter(
        (audience) =>
            (!subtype || accountAudienceSubtypeGroup(audience.subtype) === subtype) &&
            (term.length < ACCOUNT_AUDIENCE_SEARCH_MIN_LENGTH ||
                String(audience.name || '')
                    .toLowerCase()
                    .includes(term)),
    )
}

/** Error codes of the account audience requests and the fixed messages shown for them. */
export const ACCOUNT_AUDIENCE_ERROR_MESSAGES = {
    UNAUTHENTICATED: 'Your session has expired. Please sign in again.',
    FORBIDDEN: 'You do not have access to the audiences of this ad account.',
    PUBLISHER_NOT_CONNECTED:
        'Your ad account connection needs to be renewed. Reconnect it in Media Settings and try again.',
    META_READ_FAILED: 'Meta could not be reached to read your audiences. Please try again in a moment.',
}

const GENERIC_ERROR = 'Your audiences could not be loaded. Please try again.'
const NETWORK_ERROR = 'Your audiences could not be reached. Check your connection and try again.'

/** Maps an Apollo error to a fixed message. The server text is never shown. */
export const mapAccountAudiencesError = (error) => {
    const code = getErrorCode(error)
    if (code && ACCOUNT_AUDIENCE_ERROR_MESSAGES[code]) return ACCOUNT_AUDIENCE_ERROR_MESSAGES[code]
    if (error?.networkError) return NETWORK_ERROR
    return GENERIC_ERROR
}

/** Whether a refresh was refused only because the audiences were read a moment ago. */
export const isRefreshTooSoon = (error) => getErrorCode(error) === 'REFRESH_TOO_SOON'

/** The wizard id of an audience. It has no underscore, as the ad selections are keyed `<stage>_<audience>`. */
export const accountAudienceWizardId = (externalId) =>
    `${ACCOUNT_AUDIENCE_ID_PREFIX}${String(externalId).replace(/_/g, '-')}`

export const isAccountAudienceId = (id) => typeof id === 'string' && id.startsWith(ACCOUNT_AUDIENCE_ID_PREFIX)

/** The chosen account audiences of the wizard, by wizard id. */
export const getAccountAudienceMap = (data) => data?.accountAudiences || {}

/** The catalogue audience id each stage uses for account audiences, from the `stages` of the server result. */
export const stageAudienceIds = (stages) =>
    Object.fromEntries(
        (stages || [])
            .filter((entry) => entry?.stage && entry?.audience_id)
            .map((entry) => [entry.stage, entry.audience_id]),
    )

/** Whether the audience is chosen for the stage. */
export const isAccountAudienceSelected = (data, stageId, externalId) =>
    (data?.audienceSelections?.[stageId] || []).includes(accountAudienceWizardId(externalId))

/**
 * Wizard data with an audience chosen or removed for a stage. Other selections are kept; the entry of an audience
 * no stage uses any more is dropped.
 * @param {object} data the `salesFunnelSetup` data
 * @param {string} stageId
 * @param {{external_id: string, name: string, subtype?: string}} audience
 * @param {string} catalogueAudienceId the stage's catalogue audience id for account audiences
 * @param {boolean} selected
 */
export const withAccountAudience = (data, stageId, audience, catalogueAudienceId, selected) => {
    const base = data || {}
    const id = accountAudienceWizardId(audience.external_id)
    const selections = base.audienceSelections || {}
    const current = (selections[stageId] || []).filter((item) => item !== id)
    const map = { ...getAccountAudienceMap(base) }
    const stages = { ...(map[id]?.stages || {}) }
    if (selected) stages[stageId] = catalogueAudienceId
    else delete stages[stageId]
    if (Object.keys(stages).length > 0) {
        map[id] = {
            id,
            external_id: audience.external_id,
            name: audience.name,
            subtype: audience.subtype || null,
            stages,
        }
    } else {
        delete map[id]
    }
    return {
        ...base,
        audienceSelections: { ...selections, [stageId]: selected ? [...current, id] : current },
        accountAudiences: map,
    }
}

/** Wizard data without an audience in a stage, as when it is taken off the stage's list. */
export const withoutAccountAudience = (data, stageId, wizardId) => {
    const entry = getAccountAudienceMap(data)[wizardId]
    if (!entry) return data
    return withAccountAudience(data, stageId, entry, null, false)
}

/** An account audience as a catalogue audience, so the wizard's screens can list it. */
export const accountAudienceToAudience = (entry) => ({
    id: entry.id,
    name: entry.name,
    description: '',
    icon: 'TargetIcon',
    funnel_stage: Object.keys(entry.stages || {})[0] || 'acquisition',
    lookalike: false,
    ai: false,
    active: false,
    default: false,
    account_audience_id: entry.external_id,
})

/**
 * Adds the wizard's chosen account audiences to a catalogue audiences list. Returns the very same array when the
 * wizard has none, so screens behave exactly as before.
 */
export const mergeAccountAudiences = (audiences, accountMap) => {
    const entries = Object.values(accountMap || {}).filter((entry) => entry?.id)
    if (entries.length === 0) return audiences
    const base = audiences || []
    const known = new Set(base.map((audience) => audience.id))
    const missing = entries.filter((entry) => !known.has(entry.id)).map(accountAudienceToAudience)
    return missing.length > 0 ? [...base, ...missing] : audiences
}

/**
 * Applies an account audience to a funnel stage payload row: the stage's catalogue audience id replaces the
 * wizard's own id and the Meta id of the audience is added. Rows of other audiences are returned unchanged.
 */
export const withAccountAudiencePayload = (row, accountMap, stageId) => {
    const entry = accountMap?.[row.audience_id]
    const catalogueAudienceId = entry?.stages?.[stageId]
    if (!entry || !catalogueAudienceId) return row
    return { ...row, audience_id: catalogueAudienceId, account_audience_id: entry.external_id }
}

/** The audience name as used in a default ad set name, cut to a fixed length. */
export const accountAudienceDisplayName = (audienceId, name) => {
    const text = String(name || '')
    if (!isAccountAudienceId(audienceId) || text.length <= ACCOUNT_AUDIENCE_NAME_LIMIT) return text
    return text.slice(0, ACCOUNT_AUDIENCE_NAME_LIMIT).trimEnd()
}
